import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  ChartBarIcon,
  CalendarIcon,
  ClockIcon,
  CpuChipIcon,
  StarIcon,
  BoltIcon,
  DocumentDuplicateIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
} from '@heroicons/react/24/outline';

interface AnalyticsData {
  usageMetrics: {
    totalComparisons: number;
    thisMonth: number;
    lastMonth: number;
    growthRate: number;
    avgModelsPerComparison: number;
    totalCost: number;
  };
  modelPerformance: {
    model: string;
    provider: string;
    winRate: number;
    avgScore: number;
    usageCount: number;
    cost: number;
  }[];
  categoryBreakdown: {
    category: string;
    count: number;
    avgScore: number;
    growth: number;
  }[];
  timelineData: {
    date: string;
    comparisons: number;
    avgScore: number;
    cost: number;
  }[];
  promptEffectiveness: {
    lengthRange: string;
    avgScore: number;
    count: number;
  }[];
}

interface ChartProps {
  data: any[];
  title: string;
  type: 'bar' | 'line' | 'pie';
}

interface AnalyticsDashboardProps {
  avgScore?: number | null;
  favoriteModel?: string | null;
}

const SimpleChart: React.FC<ChartProps> = ({ data, title, type }) => {
  if (type === 'bar') {
    const maxValue = Math.max(...data.map(item => item.value));

    return (
      <div className="space-y-3">
        <h4 className="text-sm font-medium text-slate-300">{title}</h4>
        <div className="space-y-2">
          {data.map((item, index) => (
            <div key={index} className="flex items-center space-x-3">
              <div className="w-16 text-xs text-slate-400 truncate">{item.label}</div>
              <div className="flex-1 bg-slate-700/50 rounded-full h-2 relative overflow-hidden">
                <motion.div
                  className="h-full bg-gradient-to-r from-cyan-400 to-blue-500 rounded-full"
                  initial={{ width: 0 }}
                  animate={{ width: `${(item.value / maxValue) * 100}%` }}
                  transition={{ duration: 1, delay: index * 0.1 }}
                />
              </div>
              <div className="w-12 text-xs text-slate-300 text-right">{item.value}</div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (type === 'line') {
    return (
      <div className="space-y-3">
        <h4 className="text-sm font-medium text-slate-300">{title}</h4>
        <div className="h-32 flex items-end space-x-1">
          {data.map((item, index) => (
            <motion.div
              key={index}
              className="flex-1 bg-gradient-to-t from-cyan-400/20 to-cyan-400/60 rounded-t"
              initial={{ height: 0 }}
              animate={{ height: `${(item.value / Math.max(...data.map(d => d.value))) * 100}%` }}
              transition={{ duration: 0.8, delay: index * 0.1 }}
              title={`${item.label}: ${item.value}`}
            />
          ))}
        </div>
        <div className="flex justify-between text-xs text-slate-400">
          <span>{data[0]?.label}</span>
          <span>{data[data.length - 1]?.label}</span>
        </div>
      </div>
    );
  }

  return null;
};

export const AnalyticsDashboard: React.FC<AnalyticsDashboardProps> = ({
  avgScore,
  favoriteModel
}) => {
  const [analytics, setAnalytics] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState<'7d' | '30d' | '90d' | '1y'>('30d');

  // Mock data - replace with actual API calls
  useEffect(() => {
    const loadAnalytics = async () => {
      setLoading(true);

      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));

      setAnalytics({
        usageMetrics: {
          totalComparisons: 127,
          thisMonth: 23,
          lastMonth: 18,
          growthRate: 27.8,
          avgModelsPerComparison: 3.2,
          totalCost: 15.47
        },
        modelPerformance: [
          {
            model: 'Claude 3.5 Sonnet',
            provider: 'Anthropic',
            winRate: 42.3,
            avgScore: 8.7,
            usageCount: 87,
            cost: 6.23
          },
          {
            model: 'GPT-4o',
            provider: 'OpenAI',
            winRate: 35.6,
            avgScore: 8.4,
            usageCount: 92,
            cost: 7.14
          },
          {
            model: 'Gemini 1.5 Pro',
            provider: 'Google',
            winRate: 18.1,
            avgScore: 8.1,
            usageCount: 76,
            cost: 1.89
          },
          {
            model: 'Mistral Large',
            provider: 'Mistral',
            winRate: 4.0,
            avgScore: 7.8,
            usageCount: 34,
            cost: 0.21
          }
        ],
        categoryBreakdown: [
          { category: 'Marketing', count: 32, avgScore: 8.6, growth: 15.3 },
          { category: 'Programming', count: 28, avgScore: 8.9, growth: 22.1 },
          { category: 'Business', count: 24, avgScore: 8.4, growth: -5.2 },
          { category: 'Education', count: 21, avgScore: 8.2, growth: 8.7 },
          { category: 'Research', count: 16, avgScore: 8.5, growth: 12.4 },
          { category: 'Creative', count: 6, avgScore: 7.9, growth: -10.1 }
        ],
        timelineData: [
          { date: '2025-09-01', comparisons: 5, avgScore: 8.1, cost: 0.67 },
          { date: '2025-09-05', comparisons: 8, avgScore: 8.3, cost: 1.12 },
          { date: '2025-09-10', comparisons: 12, avgScore: 8.5, cost: 1.89 },
          { date: '2025-09-15', comparisons: 15, avgScore: 8.4, cost: 2.34 },
          { date: '2025-09-20', comparisons: 23, avgScore: 8.7, cost: 3.21 }
        ],
        promptEffectiveness: [
          { lengthRange: '0-50 chars', avgScore: 7.2, count: 12 },
          { lengthRange: '51-100 chars', avgScore: 8.1, count: 34 },
          { lengthRange: '101-200 chars', avgScore: 8.6, count: 45 },
          { lengthRange: '201-500 chars', avgScore: 8.9, count: 28 },
          { lengthRange: '500+ chars', avgScore: 8.4, count: 8 }
        ]
      });

      setLoading(false);
    };

    loadAnalytics();
  }, [timeRange]);

  const formatNumber = (num: number) => {
    if (num >= 1000) {
      return (num / 1000).toFixed(2) + 'K';
    }
    return num.toString();
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const getGrowthIcon = (growth: number) => {
    if (growth > 0) {
      return <ArrowTrendingUpIcon className="h-4 w-4 text-green-400" />;
    } else if (growth < 0) {
      return <ArrowTrendingDownIcon className="h-4 w-4 text-red-400" />;
    }
    return null;
  };

  const getGrowthColor = (growth: number) => {
    if (growth > 0) return 'text-green-400';
    if (growth < 0) return 'text-red-400';
    return 'text-slate-400';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-400 mx-auto mb-4"></div>
          <p className="text-slate-400">Loading analytics...</p>
        </div>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="text-center py-12">
        <ChartBarIcon className="h-12 w-12 text-slate-600 mx-auto mb-4" />
        <p className="text-slate-400">Unable to load analytics data</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-white">Analytics Dashboard</h2>
          <p className="text-slate-400 mt-1">
            Detailed insights into your AI comparison usage and performance
          </p>
        </div>

        {/* Time Range Selector */}
        <div className="flex items-center space-x-2">
          {['7d', '30d', '90d', '1y'].map((range) => (
            <button
              key={range}
              onClick={() => setTimeRange(range as any)}
              className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                timeRange === range
                  ? 'bg-cyan-500/20 border border-cyan-400/30 text-cyan-300'
                  : 'bg-slate-800/50 border border-slate-700/50 text-slate-300 hover:bg-slate-700/50'
              }`}
            >
              {range === '7d' ? '7 Days' : range === '30d' ? '30 Days' : range === '90d' ? '90 Days' : '1 Year'}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="bg-slate-800/30 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6"
        >
          <div className="flex items-center justify-between mb-2">
            <ArrowTrendingUpIcon className="h-8 w-8 text-amber-400" />
            <span className="text-sm text-slate-400">Average Score</span>
          </div>
          <p className="text-2xl font-bold text-white">
            {avgScore !== null && avgScore !== undefined ? `${avgScore}/10` : 'N/A'}
          </p>
          <p className="text-slate-400 text-sm">Across your comparisons</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-slate-800/30 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6"
        >
          <div className="flex items-center justify-between mb-2">
            <StarIcon className="h-8 w-8 text-purple-400" />
            <span className="text-sm text-slate-400">Favorite Model</span>
          </div>
          <p className="text-lg font-bold text-white">
            {favoriteModel || 'No data yet'}
          </p>
          <p className="text-slate-400 text-sm">Most-used or highest scoring</p>
        </motion.div>
      </div>

      {/* Prediction Challenge Stats */}
      <div>
        <h3 className="text-lg font-semibold text-white mb-4">🎯 Prediction Challenge Stats</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="bg-slate-800/30 backdrop-blur-sm border border-slate-700/50 rounded-xl p-4"
          >
            <div className="text-xs sm:text-sm text-slate-400 mb-1">Your Accuracy</div>
            <div className="text-xl sm:text-2xl font-bold text-cyan-300">67%</div>
            <p className="text-slate-400 text-xs mt-1">Challenge predictions</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-slate-800/30 backdrop-blur-sm border border-slate-700/50 rounded-xl p-4"
          >
            <div className="text-xs sm:text-sm text-slate-400 mb-1">Site Average</div>
            <div className="text-xl sm:text-2xl font-bold text-slate-300">58%</div>
            <p className="text-slate-400 text-xs mt-1">All users average</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
            className="bg-slate-800/30 backdrop-blur-sm border border-slate-700/50 rounded-xl p-4"
          >
            <div className="text-xs sm:text-sm text-slate-400 mb-1">Correct Predictions</div>
            <div className="text-xl sm:text-2xl font-bold text-green-400">12</div>
            <p className="text-slate-400 text-xs mt-1">Total wins</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-slate-800/30 backdrop-blur-sm border border-slate-700/50 rounded-xl p-4"
          >
            <div className="text-xs sm:text-sm text-slate-400 mb-1">Your Rank</div>
            <div className="text-xl sm:text-2xl font-bold text-yellow-400">#142</div>
            <p className="text-slate-400 text-xs mt-1">Global leaderboard</p>
          </motion.div>
        </div>
      </div>

      {/* Usage Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-slate-800/30 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6"
        >
          <div className="flex items-center justify-between mb-2">
            <DocumentDuplicateIcon className="h-8 w-8 text-cyan-400" />
            <div className="flex items-center space-x-1">
              {getGrowthIcon(analytics.usageMetrics.growthRate)}
              <span className={`text-sm font-medium ${getGrowthColor(analytics.usageMetrics.growthRate)}`}>
                {analytics.usageMetrics.growthRate > 0 ? '+' : ''}{analytics.usageMetrics.growthRate.toFixed(2)}%
              </span>
            </div>
          </div>
          <p className="text-2xl font-bold text-white">{analytics.usageMetrics.totalComparisons}</p>
          <p className="text-slate-400 text-sm">Total Comparisons</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-slate-800/30 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6"
        >
          <div className="flex items-center justify-between mb-2">
            <CalendarIcon className="h-8 w-8 text-green-400" />
            <span className="text-sm text-slate-400">This Month</span>
          </div>
          <p className="text-2xl font-bold text-white">{analytics.usageMetrics.thisMonth}</p>
          <p className="text-slate-400 text-sm">vs {analytics.usageMetrics.lastMonth} last month</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-slate-800/30 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6"
        >
          <div className="flex items-center justify-between mb-2">
            <CpuChipIcon className="h-8 w-8 text-purple-400" />
            <span className="text-sm text-slate-400">Average</span>
          </div>
          <p className="text-2xl font-bold text-white">{analytics.usageMetrics.avgModelsPerComparison}</p>
          <p className="text-slate-400 text-sm">Models per comparison</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-slate-800/30 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6"
        >
          <div className="flex items-center justify-between mb-2">
            <BoltIcon className="h-8 w-8 text-amber-400" />
            <span className="text-sm text-slate-400">Total Spend</span>
          </div>
          <p className="text-2xl font-bold text-white">{formatCurrency(analytics.usageMetrics.totalCost)}</p>
          <p className="text-slate-400 text-sm">API usage cost</p>
        </motion.div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Model Performance */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-slate-800/30 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6"
        >
          <h3 className="text-lg font-semibold text-white mb-6 flex items-center">
            <ArrowTrendingUpIcon className="h-5 w-5 mr-2" />
            Model Performance
          </h3>

          <div className="space-y-4">
            {analytics.modelPerformance.map((model, index) => (
              <motion.div
                key={model.model}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.6 + index * 0.1 }}
                className="flex items-center justify-between p-4 bg-slate-900/50 rounded-xl"
              >
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-1">
                    <h4 className="font-medium text-white">{model.model}</h4>
                    <span className="text-xs text-slate-400">{model.provider}</span>
                  </div>
                  <div className="flex items-center space-x-4 text-sm text-slate-400">
                    <span>Win Rate: {model.winRate}%</span>
                    <span>Avg: {model.avgScore}/10</span>
                    <span>Used: {model.usageCount}x</span>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-white font-semibold">{formatCurrency(model.cost)}</div>
                  <div className="text-xs text-slate-400">Total cost</div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Category Breakdown */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="bg-slate-800/30 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6"
        >
          <h3 className="text-lg font-semibold text-white mb-6 flex items-center">
            <ChartBarIcon className="h-5 w-5 mr-2" />
            Category Performance
          </h3>

          <div className="space-y-4">
            {analytics.categoryBreakdown.map((category, index) => (
              <motion.div
                key={category.category}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.7 + index * 0.1 }}
                className="flex items-center justify-between p-3 bg-slate-900/50 rounded-lg"
              >
                <div>
                  <div className="font-medium text-white">{category.category}</div>
                  <div className="text-sm text-slate-400">
                    {category.count} comparisons • Avg: {category.avgScore}/10
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  {getGrowthIcon(category.growth)}
                  <span className={`text-sm font-medium ${getGrowthColor(category.growth)}`}>
                    {category.growth > 0 ? '+' : ''}{category.growth.toFixed(2)}%
                  </span>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Usage Timeline */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="bg-slate-800/30 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6"
        >
          <h3 className="text-lg font-semibold text-white mb-6 flex items-center">
            <ClockIcon className="h-5 w-5 mr-2" />
            Usage Timeline
          </h3>

          <SimpleChart
            data={analytics.timelineData.map(item => ({
              label: new Date(item.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
              value: item.comparisons
            }))}
            title="Comparisons over time"
            type="line"
          />
        </motion.div>

        {/* Prompt Effectiveness */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9 }}
          className="bg-slate-800/30 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6"
        >
          <h3 className="text-lg font-semibold text-white mb-6 flex items-center">
            <StarIcon className="h-5 w-5 mr-2" />
            Prompt Effectiveness
          </h3>

          <SimpleChart
            data={analytics.promptEffectiveness.map(item => ({
              label: item.lengthRange,
              value: item.avgScore
            }))}
            title="Average score by prompt length"
            type="bar"
          />

          <div className="mt-4 p-3 bg-cyan-500/10 border border-cyan-400/30 rounded-lg">
            <p className="text-cyan-300 text-sm">
              💡 <strong>Insight:</strong> Prompts between 101-200 characters tend to perform best with an average score of 8.6/10
            </p>
          </div>
        </motion.div>
      </div>

      {/* Export Options */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.0 }}
        className="bg-slate-800/30 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6"
      >
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-white mb-2">Export Analytics</h3>
            <p className="text-slate-400 text-sm">
              Download your analytics data for further analysis
            </p>
          </div>
          <div className="flex items-center space-x-3">
            <button className="px-4 py-2 bg-blue-500/20 border border-blue-400/30 text-blue-300 rounded-lg hover:bg-blue-500/30 transition-colors">
              Export CSV
            </button>
            <button className="px-4 py-2 bg-green-500/20 border border-green-400/30 text-green-300 rounded-lg hover:bg-green-500/30 transition-colors">
              Export PDF
            </button>
            <button className="px-4 py-2 bg-purple-500/20 border border-purple-400/30 text-purple-300 rounded-lg hover:bg-purple-500/30 transition-colors">
              Export JSON
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default AnalyticsDashboard;